import React, { useCallback } from 'react'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { LazyHydrate } from '@/atoms/LazyHydrate'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { HeadingSM, HeadingXL, ParagraphMD, ParagraphSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { BoundedImage } from '@/molecules/BoundedImage'
import { DocumentScroller } from '@/organisms/DocumentScroller'
import { Downloadable } from '@/services/DownloadablesService'
import { ProjectMeta } from '@/services/ProjectsService'
import { useSafeTrack } from '@/utils/analytics'
import { isDefined } from '@/utils/primitives/generics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

export interface WatchProjectDownloadsViewProps {
  downloadables: Downloadable[]
  projectMeta: ProjectMeta
  isLoggedIn: boolean
}

export const WatchProjectDownloadsView: React.FC<WatchProjectDownloadsViewProps> = ({
  downloadables,
  projectMeta,
  isLoggedIn,
}) => {
  const { t } = useTranslate('common')
  const track = useSafeTrack()

  const hasDownloadables = downloadables && downloadables.length > 0
  const titleLogo = projectMeta?.title && 'logo' in projectMeta.title ? projectMeta.title.logo : null
  const logoUrl =
    titleLogo && typeof titleLogo === 'object' && titleLogo !== null && 'cloudinaryPath' in titleLogo
      ? (titleLogo.cloudinaryPath as string)
      : projectMeta?.logoCloudinaryPath
  const projectName = projectMeta?.name
  const projectSlug = projectMeta?.slug

  const handleCtaClick = useCallback(() => {
    track('Watch Downloads Page Cta Clicked', {
      program: projectSlug,
      title: projectName,
    })
  }, [track, projectSlug, projectName])

  return (
    <div className="min-h-screen bg-core-gray-950 pb-20 pt-28 text-white">
      <PaddedContainer fluid>
        <div className="mb-12">
          {logoUrl && (
            <BoundedImage
              className="mb-2 flex items-center justify-center md:justify-start lg:mb-4"
              alt={projectMeta?.name}
              path={logoUrl}
              width={600}
              height={104}
            />
          )}
          <HeadingXL weight="bold" className="mb-4">
            {t('downloads', 'Downloads')}
          </HeadingXL>
          <ParagraphMD color="core-gray-500">
            <Translate t={t} i18nKey="watchProjectDownloadsDescription" values={{ projectName }}>
              Download coloring pages, activities, and recipes for <i>{{ projectName }}</i>. Perfect for family fun time
              and homeschool activities!
            </Translate>
          </ParagraphMD>
        </div>

        <When condition={!hasDownloadables}>
          <div className="py-20 text-left">
            <HeadingSM weight="semibold" className="mb-4">
              {t('noDownloadsAvailable', 'No downloads available at this time')}
            </HeadingSM>
            <ParagraphSM color="core-gray-400">
              {t('checkBackLaterDownloads', 'Check back later for new downloadable content')}
            </ParagraphSM>
          </div>
        </When>

        <When condition={hasDownloadables}>
          <LazyHydrate whenVisible>
            <DocumentScroller
              authenticated={Boolean(isLoggedIn)}
              downloadables={downloadables.filter((d) => isDefined(d) && d.href)}
              project={projectSlug}
              customTrackEventName="Watch Downloads Page Downloadable Clicked"
              returnTo={`${paths.watch.index}/${projectSlug}/downloads`}
              className="grid h-full w-full grid-cols-2 !p-0 md:flex md:flex-row md:flex-wrap"
              cardClassName="w-fit md:w-[268px]"
              imageClassName="md:!h-[338px] md:!w-[252px] !w-[168px] !h-[224px] se:!w-[150px] se:!h-[200px]"
            />
          </LazyHydrate>
        </When>

        <LinkButton
          onClick={handleCtaClick}
          href={`${paths.watch.index}/${projectSlug}`}
          className="mt-12 w-full border-transparent bg-core-gray-800 hover:bg-core-gray-800/50 md:w-fit"
          variant={'core-gray-700'}
        >
          {t('watchProject', 'Watch {{ projectName }}', { projectName })}
        </LinkButton>
      </PaddedContainer>
    </div>
  )
}
